import { Dispatch, MutableRefObject, SetStateAction } from 'react';

import { resolveMessageId } from './event-helpers';
import {
  AgUiEvent,
  ConnectionStatus,
  GatewayMessage,
  GatewayMessageMetadata,
  GatewayProgressState,
} from '../types/gateway-chat-types';

/**
 * Context object containing all state setters needed by event handlers.
 */
export interface EventHandlerContext {
  setMessages: Dispatch<SetStateAction<GatewayMessage[]>>;
  setConnectionStatus: Dispatch<SetStateAction<ConnectionStatus>>;
  setProgressState: Dispatch<SetStateAction<GatewayProgressState>>;
  setCurrentRunId: (runId: string) => void;
  currentRunIdRef: MutableRefObject<string>;
  createInitialProgressState: () => GatewayProgressState;
}

/**
 * Handles CONNECTION_ESTABLISHED events.
 */
export const handleConnectionEstablished = (ctx: EventHandlerContext): void => {
  ctx.setConnectionStatus('connected');
};

/**
 * Handles CONNECTION_CLOSED events.
 */
export const handleConnectionClosed = (ctx: EventHandlerContext): void => {
  ctx.setConnectionStatus('disconnected');
};

/**
 * Handles SESSION_HISTORY events by loading historical messages.
 */
export const handleSessionHistory = (event: AgUiEvent, ctx: EventHandlerContext): void => {
  const messages = event.payload.messages;
  const historyMessages = Array.isArray(messages) ? messages : [];

  ctx.setMessages(
    historyMessages.map((msg: unknown, index: number) => {
      const message = msg as Record<string, unknown>;
      return {
        id: resolveMessageId(msg, index),
        role: message.role as 'user' | 'assistant',
        content: String(message.content ?? ''),
        status: 'complete' as const,
        timestamp: String(message.timestamp ?? new Date().toISOString()),
        metadata:
          message.metadata && typeof message.metadata === 'object'
            ? (message.metadata as GatewayMessageMetadata)
            : undefined,
      };
    })
  );
};

/**
 * Handles RUN_STARTED events by creating a new assistant message.
 */
export const handleRunStarted = (event: AgUiEvent, ctx: EventHandlerContext): void => {
  const runIdFromEvent = event.runId ?? undefined;
  const nextRunId = runIdFromEvent ?? `run-${Date.now()}`;

  ctx.setCurrentRunId(nextRunId);
  ctx.setProgressState({ percentage: 0, message: 'Starting...', active: true });
  ctx.setMessages((prev) => [
    ...prev,
    {
      id: nextRunId,
      role: 'assistant',
      content: '',
      status: 'streaming',
      timestamp: event.timestamp,
      metadata:
        event.payload.metadata && typeof event.payload.metadata === 'object'
          ? (event.payload.metadata as GatewayMessageMetadata)
          : undefined,
    },
  ]);
};

/**
 * Handles PROGRESS_UPDATE events.
 */
export const handleProgressUpdate = (event: AgUiEvent, ctx: EventHandlerContext): void => {
  ctx.setProgressState({
    percentage: typeof event.payload.percentage === 'number' ? event.payload.percentage : 0,
    message: typeof event.payload.message === 'string' ? event.payload.message : 'Processing...',
    active: true,
  });
};

/**
 * Handles TEXT_MESSAGE_START events.
 */
export const handleTextMessageStart = (
  effectiveRunId: string | undefined,
  ctx: EventHandlerContext
): void => {
  if (!effectiveRunId) {
    return;
  }

  // Hide progress during streaming - the message itself shows streaming state
  ctx.setProgressState(ctx.createInitialProgressState());
  ctx.setMessages((prev) =>
    prev.map((msg) =>
      msg.id === effectiveRunId ? { ...msg, status: 'streaming', content: '' } : msg
    )
  );
};

/**
 * Handles TEXT_MESSAGE_CONTENT events by appending content to the message.
 */
export const handleTextMessageContent = (
  event: AgUiEvent,
  effectiveRunId: string | undefined,
  ctx: EventHandlerContext
): void => {
  if (!effectiveRunId) {
    return;
  }

  ctx.setMessages((prev) =>
    prev.map((msg) =>
      msg.id === effectiveRunId
        ? {
            ...msg,
            content:
              msg.content +
              (typeof event.payload.content === 'string' ? event.payload.content : ''),
          }
        : msg
    )
  );
};

/**
 * Handles TEXT_MESSAGE_END events.
 */
export const handleTextMessageEnd = (
  effectiveRunId: string | undefined,
  ctx: EventHandlerContext
): void => {
  if (!effectiveRunId) {
    return;
  }

  ctx.setMessages((prev) =>
    prev.map((msg) => (msg.id === effectiveRunId ? { ...msg, status: 'complete' } : msg))
  );
};

/**
 * Handles ERROR events.
 */
export const handleError = (
  event: AgUiEvent,
  effectiveRunId: string | undefined,
  ctx: EventHandlerContext
): void => {
  if (!effectiveRunId) {
    ctx.setCurrentRunId('');
    ctx.setProgressState(ctx.createInitialProgressState());
    return;
  }

  ctx.setMessages((prev) =>
    prev.map((msg) =>
      msg.id === effectiveRunId
        ? {
            ...msg,
            content:
              msg.content ||
              `Error: ${
                typeof event.payload.message === 'string' ? event.payload.message : 'Unknown error'
              }`,
            status: 'error',
          }
        : msg
    )
  );
  ctx.setCurrentRunId('');
  ctx.setProgressState(ctx.createInitialProgressState());
};

/**
 * Handles RUN_FINISHED events.
 */
export const handleRunFinished = (
  event: AgUiEvent,
  effectiveRunId: string | undefined,
  ctx: EventHandlerContext
): void => {
  if (!effectiveRunId) {
    ctx.setCurrentRunId('');
    ctx.setProgressState(ctx.createInitialProgressState());
    return;
  }

  ctx.setMessages((prev) =>
    prev.map((msg) =>
      msg.id === effectiveRunId
        ? {
            ...msg,
            status: 'complete',
            metadata:
              event.payload.metadata && typeof event.payload.metadata === 'object'
                ? (event.payload.metadata as GatewayMessageMetadata)
                : msg.metadata,
          }
        : msg
    )
  );
  ctx.setCurrentRunId('');
  ctx.setProgressState(ctx.createInitialProgressState());
};

/**
 * Handles METADATA events from the gateway.
 * If the metadata contains omop_session_info, processes it for OMOP session tracking.
 */
export const handleMetadata = (event: AgUiEvent, ctx: EventHandlerContext): void => {
  // Check if this metadata event contains OMOP session info
  const omopSessionInfo = event.payload.omop_session_info;

  if (omopSessionInfo && typeof omopSessionInfo === 'object') {
    const { session_id, file_name, original_file_name, total_codes, s3_uri, status } =
      omopSessionInfo as Record<string, unknown>;

    // Store session info in localStorage for persistence across page refreshes
    if (typeof session_id === 'string') {
      localStorage.setItem('omop_session_id', session_id);
    }
    if (typeof file_name === 'string') {
      localStorage.setItem('omop_file_name', file_name);
    }
    if (typeof original_file_name === 'string') {
      localStorage.setItem('omop_original_file_name', original_file_name);
    }
    if (typeof total_codes === 'number') {
      localStorage.setItem('omop_total_codes', total_codes.toString());
    }

    // Update the current message with OMOP session info in metadata
    const currentRunId = ctx.currentRunIdRef.current;
    if (currentRunId) {
      ctx.setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentRunId
            ? {
                ...msg,
                metadata: {
                  ...msg.metadata,
                  omop_session_info: {
                    session_id: String(session_id),
                    file_name: String(file_name),
                    original_file_name: original_file_name ? String(original_file_name) : undefined,
                    total_codes: typeof total_codes === 'number' ? total_codes : undefined,
                    s3_uri: s3_uri ? String(s3_uri) : undefined,
                    status: status ? String(status) : undefined,
                  },
                },
              }
            : msg
        )
      );
    }

    // Dispatch custom event for other components to listen to
    window.dispatchEvent(
      new CustomEvent('omop-session-updated', {
        detail: { session_id, file_name, original_file_name, total_codes, s3_uri, status },
      })
    );
  }
};
